import { Component as ReactComponent, Suspense, createContext, forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { ErrorInfo, ReactNode } from "react";
import type { ActionDef, ClientActionRenderPlan, FormButtonElement } from "../core";
import { mapClientActionPlan } from "../core/services/mappers";
import { isChunkLoadError } from "../react/lazyWithRetry";
import { useSession } from "../react/providers/SessionProvider";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { ClientActionLayoutRenderer } from "./ClientActionLayoutRenderer";
import { confirm } from "./ConfirmDialog";
import {
    getClientActionDescriptor,
    isZodSchema,
    type ClientActionParams,
    type ClientActionParamSchema,
} from "./ClientActionRegistry";

// ClientActionPage — the unified host for `action_type="client"` actions.
// Replaces the two parallel mount sites (ActionManager's client branch
// and DialogActionHost's client branch) with a single component that
// owns:
//
//   1. Registry lookup + missing-key fallback
//   2. Param-shape validation against the descriptor's paramSchema
//   3. Permission gate against the descriptor's `requires` (forward-
//      compat — today's UserIdentity lacks roles; the gate is wired
//      and will activate once roles land in the session payload)
//   4. Descriptor-level onOpen gate — render is held on a "Loading…"
//      placeholder until the async setup hook resolves
//   5. <Suspense> wrapping for lazy-loaded components
//   6. Lifecycle orchestration — `onClose` veto before close (with
//      themed confirm) for dialog mounts; `onSave` via Cmd+S keybind
//   7. Lifecycle context provider so child components register their
//      dynamic callbacks via `useClientActionLifecycle()`
//   8. Imperative `tryClose()` handle exposed via ref — DialogActionHost
//      calls it on X / Escape / outside-click to run the veto chain
//      before the dialog frame pops
//
// The host distinguishes between PAGE mounts (workspace route, owns
// no close button) and DIALOG mounts (Radix Dialog frame, owns the X
// + outer close). PAGE close has no veto (navigation is the user's
// intent); DIALOG close consults the registered onClose first.

const _UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Lift `URLSearchParams` to a plain Record. Multi-valued query params
    collapse to a single string (last wins) — zod schemas wanting array
    fields can declare `z.string().array()` and parse via a custom
    transform, but the simple case is single-value per key which is
    what every real consumer needs. */
function _collectQueryParams(): Record<string, string> {
    if (typeof window === "undefined") return {};
    const out: Record<string, string> = {};
    try {
        const qs = new URLSearchParams(window.location.search || "");
        qs.forEach((v, k) => { out[k] = v; });
    } catch {
        // pre-jsdom test environments / SSR — gracefully degrade
    }
    return out;
}

/** Build the raw input the schema validates against. Starts from query
    string (so URL filters like `?status=draft` flow through) + overlays
    the action's record-uuid slot under the well-known `param` key. */
function _buildRawParams(param: string | undefined): Record<string, unknown> {
    const raw: Record<string, unknown> = { ..._collectQueryParams() };
    if (param !== undefined && param !== "") {
        raw.param = param;
    }
    return raw;
}

type _ParamResult =
    | { ok: true; params: ClientActionParams }
    | { ok: false; reason: string };

function validateParam(
    schema: ClientActionParamSchema | undefined,
    param: string | undefined,
): _ParamResult {
    // Build the raw input once — same shape every path consumes.
    const raw = _buildRawParams(param);

    // Zod path — full object-shape validation. The parsed result
    // becomes the `params` object handed to the component. Field
    // errors aggregate into a single human-readable line.
    if (isZodSchema(schema)) {
        const result = schema.safeParse(raw);
        if (!result.success) {
            const flat = result.error.issues
                .map((iss) => {
                    const path = iss.path.length > 0 ? iss.path.join(".") : "(root)";
                    return `${path}: ${iss.message}`;
                })
                .join("; ");
            return { ok: false, reason: flat || "schema validation failed" };
        }
        return { ok: true, params: (result.data ?? {}) as ClientActionParams };
    }

    // Legacy string-discriminator path — validates the `param` slot
    // only; `params` becomes `{param}` for the component (or `{}` when
    // empty / "any" schema).
    if (schema === undefined || schema === "any") {
        return { ok: true, params: raw };
    }
    if (schema === "uuid?") {
        if (param === undefined || param === "") return { ok: true, params: raw };
        return _UUID_RE.test(param)
            ? { ok: true, params: raw }
            : { ok: false, reason: `expected uuid, got "${param}"` };
    }
    if (schema === "uuid") {
        if (!param) return { ok: false, reason: "missing required uuid param" };
        return _UUID_RE.test(param)
            ? { ok: true, params: raw }
            : { ok: false, reason: `expected uuid, got "${param}"` };
    }
    return { ok: true, params: raw };
}

// ── Lifecycle context ─────────────────────────────────────────────────────
//
// The client component (mounted as a child of ClientActionPage) calls
// `useClientActionLifecycle({ onClose, onSave, ... })` once in its
// render — the hook stashes the callbacks in the surrounding context so
// the page host can invoke them on close / save events. Components that
// don't need lifecycle just skip the hook.

export interface ClientActionLifecycleContext {
    /** What surface mounted the action — components may behave
        differently in dialog mode (e.g. auto-close after save). */
    mountedAs: "page" | "dialog";
    /** Programmatic close request, only meaningful when mountedAs="dialog".
        Page mounts navigate away through normal router events. */
    requestClose?: () => void;
}

export interface ClientActionLifecycleHandlers {
    /** Veto close. Returning `{canClose: false, reason}` shows a
        themed confirm dialog ("reason" is the body text). Returning
        `{canClose: true}` (or undefined) allows close to proceed. */
    onClose?: (ctx: ClientActionLifecycleContext) => Promise<{ canClose: boolean; reason?: string }> | { canClose: boolean; reason?: string };
    /** Standard save semantics — bound to Cmd/Ctrl + S inside the
        client surface. Components implement persistence here. */
    onSave?: (ctx: ClientActionLifecycleContext) => Promise<void> | void;
}

interface _LifecycleInternal {
    ctx: ClientActionLifecycleContext;
    handlersRef: { current: ClientActionLifecycleHandlers };
    /** Optional title-override sink. When the client action lives inside
        a DialogActionHost, this is wired to the dialog's title state so
        the component can replace the static `ir.action.name` with a
        record-specific string (e.g. "20ft GP Container (Container ›
        Dry Container)"). In page mode the field is undefined and the
        `useClientActionTitle()` hook is a no-op. */
    setTitleOverride?: (title: string | null) => void;
}

const _LifecycleCtx = createContext<_LifecycleInternal | null>(null);

/** Set a record-specific override for the client-action dialog title.
    Pass `null` to revert to the action's default name. The hook is a
    no-op when the action is mounted as a page (the page mode doesn't
    own a chrome surface yet — extends naturally when it does).

        function Equipment3DPage({ param }) {
            const spec = useSpec(param);
            useClientActionTitle(spec ? `${spec.name} (${spec.path})` : null);
            ...
        } */
export function useClientActionTitle(title: string | null): void {
    const internal = useContext(_LifecycleCtx);
    const setter = internal?.setTitleOverride;
    useEffect(() => {
        if (!setter) return;
        setter(title);
        return () => setter(null);
    }, [setter, title]);
}

/** Child-side hook. Components register their lifecycle handlers via:
 *
 *    useClientActionLifecycle({
 *        onClose: async () => isDirty ? { canClose: false, reason: "Discard?" } : { canClose: true },
 *        onSave: async () => { await persist(); },
 *    });
 *
 * Handlers re-register on every render (cheap — they're stashed in a
 * ref). Returns the lifecycle context so the component can read
 * `mountedAs` / `requestClose`.
 */
export function useClientActionLifecycle(handlers: ClientActionLifecycleHandlers): ClientActionLifecycleContext {
    const internal = useContext(_LifecycleCtx);
    if (!internal) {
        throw new Error(
            "useClientActionLifecycle must be used inside <ClientActionPage>. "
            + "If you're a client-action component, the page host wraps you automatically.",
        );
    }
    // Mutate the ref so the latest handlers fire on every event without
    // forcing a re-render of the host or the registered handlers.
    internal.handlersRef.current = handlers;
    return internal.ctx;
}

// ── Imperative handle ─────────────────────────────────────────────────────

/** Exposed via `forwardRef<ClientActionPageHandle>`. DialogActionHost
    holds a ref to the page and calls `tryClose()` from its
    `onOpenChange` to make X / Escape / outside-click respect the
    registered `onClose` veto. Returns true if close should proceed
    (no handler, allowed, OR user confirmed the discard); false when
    vetoed AND the user cancelled the confirm. */
export interface ClientActionPageHandle {
    tryClose: () => Promise<boolean>;
}

/** Run the registered onClose handler + show the discard confirm on
    veto. Pure function (returns boolean; doesn't trigger close itself).
    Shared by both the component-initiated `ctx.requestClose()` path
    and the dialog-chrome-initiated `tryClose()` imperative handle.
    Module-level so it's testable without React mounting. */
async function runCloseVeto(
    handlers: ClientActionLifecycleHandlers,
    ctx: ClientActionLifecycleContext,
): Promise<boolean> {
    const onClose = handlers.onClose;
    if (!onClose) return true;
    const result = await Promise.resolve(onClose(ctx));
    if (result.canClose) return true;
    return confirm({
        title: "Discard changes?",
        message: result.reason ?? "You have unsaved changes. Close anyway?",
        confirmLabel: "Discard",
        cancelLabel: "Keep editing",
        variant: "danger",
    });
}

// ── Page host ─────────────────────────────────────────────────────────────

interface ClientActionPageProps {
    action: ActionDef;
    param?: string;
    mountedAs: "page" | "dialog";
    /** Wired only when mountedAs="dialog". Called after the registered
        onClose returns canClose=true (or the user confirms the veto
        dialog). Page mounts use router navigation instead. */
    onRequestClose?: () => void;
    /** Wired only when mountedAs="dialog". Lets the registered client
        component override the dialog's title (action.name fallback)
        via the `useClientActionTitle()` hook. */
    onTitleChange?: (title: string | null) => void;
}

export const ClientActionPage = forwardRef<ClientActionPageHandle, ClientActionPageProps>(function ClientActionPage(
    { action, param, mountedAs, onRequestClose, onTitleChange },
    ref,
) {
    const key = action.clientComponent ?? null;
    const descriptor = key ? getClientActionDescriptor(key) : null;

    // ── All hooks must run on every render before any early returns ──────
    // React's rules-of-hooks: hook count + order MUST be stable across
    // renders. Any conditional early-return BEFORE a hook would change
    // the call sequence when the condition flips. We call every hook
    // (useRef / useImperativeHandle / useSession) up front; the early
    // returns below skip mounting, not hook execution.
    const vetoFnRef = useRef<() => Promise<boolean>>(async () => true);
    useImperativeHandle(ref, () => ({
        tryClose: () => vetoFnRef.current(),
    }), []);
    const session = useSession();

    // ── 1. Missing-key fallback ──────────────────────────────────────────
    if (!descriptor) {
        return (
            <div className="client-action-host client-action-host--missing">
                Unknown client component:{" "}
                <span className="client-action-host__key">{key ?? "(none)"}</span>
            </div>
        );
    }

    // ── 2. Param-shape validation ────────────────────────────────────────
    const paramCheck = validateParam(descriptor.paramSchema, param);
    if (!paramCheck.ok) {
        return (
            <div className="client-action-host client-action-host--missing">
                Bad URL parameter for{" "}
                <span className="client-action-host__key">{descriptor.key}</span>:{" "}
                {paramCheck.reason}
            </div>
        );
    }
    const parsedParams = paramCheck.params;

    // ── 3. Permission gate ───────────────────────────────────────────────
    // descriptor.requires is an AND'd list of RBAC role codes the user
    // must carry. Empty / absent → no gate. Missing roles → render the
    // forbidden placeholder; do NOT mount the component (avoids
    // expensive component setup for a user who can't see it anyway).
    if (descriptor.requires && descriptor.requires.length > 0) {
        const userRoles = new Set(session.roles ?? []);
        const missing = descriptor.requires.filter((r) => !userRoles.has(r));
        if (missing.length > 0) {
            return (
                <div className="client-action-host client-action-host--missing">
                    Forbidden — <span className="client-action-host__key">{descriptor.key}</span>{" "}
                    requires role{missing.length === 1 ? "" : "s"}: <code>{missing.join(", ")}</code>.
                </div>
            );
        }
    }

    // ── 4. Mount (lifecycle context + onOpen gate + Suspense) ────────────
    return (
        <_ClientActionMount
            descriptor={descriptor}
            action={action}
            param={param}
            params={parsedParams}
            mountedAs={mountedAs}
            onRequestClose={onRequestClose}
            onTitleChange={onTitleChange}
            vetoFnRef={vetoFnRef}
        />
    );
});

interface _MountProps extends ClientActionPageProps {
    descriptor: NonNullable<ReturnType<typeof getClientActionDescriptor>>;
    /** Mutated to point at the live veto function once the component
        mounts — gives ClientActionPage's imperative handle a way to
        reach the per-mount handlers without prop-drilling refs. */
    vetoFnRef: { current: () => Promise<boolean> };
    /** Parsed + validated params (the result of paramSchema validation
        in the outer ClientActionPage). Passed through to the component
        + the onOpen hook. */
    params: ClientActionParams;
}

function _ClientActionMount({ descriptor, action, param, params, mountedAs, onRequestClose, onTitleChange, vetoFnRef }: _MountProps) {
    const Component = descriptor.component;
    const runtime = useRuntime();
    const handlersRef = useRef<ClientActionLifecycleHandlers>({});
    const [closing, setClosing] = useState(false);
    // Optional layout-XML — when `descriptor.layout` is set, fetch the
    // parsed `<ClientActionView>` plan once and wrap the component in
    // the layout's header/body/footer chrome. Null = "no layout
    // declared, mount the component bare" (today's default).
    const [layoutPlan, setLayoutPlan] = useState<ClientActionRenderPlan | null | "pending">(
        descriptor.layout ? "pending" : null,
    );
    useEffect(() => {
        if (!descriptor.layout) return;
        let cancelled = false;
        const { modelKey } = descriptor.layout;
        runtime.services.action
            .loadModelView(modelKey, "clientaction")
            .then((load) => {
                if (cancelled) return;
                const plan = load?.view ? mapClientActionPlan(load.view) : null;
                setLayoutPlan(plan);
            })
            .catch(() => {
                if (!cancelled) setLayoutPlan(null);
            });
        return () => { cancelled = true; };
    }, [descriptor, runtime]);

    // Imperative DOM ref collection — populated as `<container ref="X"/>`
    // slots mount. Future imperative-style plugins read these via a
    // hook (`useClientActionLayoutRefs()`). Held in a ref to avoid
    // re-renders on every container mount.
    const layoutRefs = useRef<Record<string, HTMLDivElement>>({});
    const registerLayoutRef = useCallback((name: string, el: HTMLDivElement | null) => {
        if (el) layoutRefs.current[name] = el;
        else delete layoutRefs.current[name];
    }, []);
    const handleLayoutCommand = useCallback((button: FormButtonElement) => {
        // For now, forward the layout-button click as a synthetic onSave
        // / onCommand-like signal — components handle this through the
        // existing lifecycle hooks. A dedicated `onLayoutCommand(name)`
        // hook lands when the first plugin-style adopter (Equipment 3D
        // viewer) needs distinct routing.
        const onSave = handlersRef.current.onSave;
        if (button.name === "save" && onSave) {
            void Promise.resolve(onSave({ mountedAs, requestClose: undefined }));
        }
        // Other button names: components can subscribe via DOM events
        // on the rendered button (data-button-name attribute), or wait
        // for the dedicated layout-command hook in the next slice.
    }, [mountedAs]);

    // Descriptor-level onOpen gate. We hold render until the optional
    // async setup hook resolves so components that need pre-mount
    // setup (load drafts, fetch prerequisites) don't paint with stale
    // state. `null` = pending; "ready" = mount; Error = the setup hook
    // threw, surface a clear error placeholder instead of mounting
    // with broken state.
    const [openState, setOpenState] = useState<"pending" | "ready" | { error: string }>(
        descriptor.onOpen ? "pending" : "ready",
    );
    useEffect(() => {
        if (!descriptor.onOpen) return;
        let cancelled = false;
        setOpenState("pending");
        Promise.resolve(descriptor.onOpen({ action, param, params }))
            .then(() => {
                if (!cancelled) setOpenState("ready");
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const msg = err instanceof Error ? err.message : String(err);
                setOpenState({ error: msg });
            });
        return () => {
            cancelled = true;
        };
    }, [descriptor, action, param, params]);

    // Lifecycle context — `requestClose` for dialog mounts routes
    // through the same veto chain the imperative handle uses, then
    // calls onRequestClose() if allowed. Both code paths share the
    // pure `runCloseVeto` helper so a component-initiated close and a
    // dialog-X close go through identical veto / confirm behaviour.
    const lifecycleCtx = useMemo<ClientActionLifecycleContext>(
        () => ({
            mountedAs,
            requestClose:
                mountedAs === "dialog"
                    ? () => {
                        if (closing || !onRequestClose) return;
                        setClosing(true);
                        void runCloseVeto(handlersRef.current, { mountedAs, requestClose: undefined })
                            .then((ok) => {
                                if (ok) onRequestClose();
                            })
                            .finally(() => setClosing(false));
                    }
                    : undefined,
        }),
        [mountedAs, closing, onRequestClose],
    );

    // Mutate the imperative-handle veto function on every render so
    // the LATEST handlers are what tryClose() consults. Pure ref-
    // mutate (no useEffect) keeps the path synchronous with state.
    vetoFnRef.current = async () => {
        if (closing) return false;
        return runCloseVeto(handlersRef.current, lifecycleCtx);
    };

    const internal = useMemo<_LifecycleInternal>(
        () => ({ ctx: lifecycleCtx, handlersRef, setTitleOverride: onTitleChange }),
        [lifecycleCtx, onTitleChange],
    );

    // Cmd/Ctrl + S keybind → onSave (when registered)
    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "s") return;
            const save = handlersRef.current.onSave;
            if (!save) return;
            e.preventDefault();
            void Promise.resolve(save(lifecycleCtx));
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lifecycleCtx]);

    // onOpen gate — show a Loading placeholder while pending, or a
    // failed-setup placeholder when the hook threw.
    if (openState === "pending") {
        return <div className="client-action-host client-action-host--loading">Loading…</div>;
    }
    if (typeof openState === "object" && "error" in openState) {
        return (
            <div className="client-action-host client-action-host--missing">
                Failed to open <span className="client-action-host__key">{descriptor.key}</span>: {openState.error}
            </div>
        );
    }

    // Layout-XML gate — when the descriptor declares a layout view,
    // hold render until that fetch resolves; then wrap the component
    // in the parsed layout's chrome. The component mounts into the
    // layout's `<component/>` slot via the renderer's `children` prop.
    // Layouts without a `<component/>` slot are pure imperative —
    // the component receives `refs` via the lifecycle hook (TODO when
    // first imperative-plugin adopter lands) and uses them to mount
    // external libs.
    const componentNode = (
        <ClientActionErrorBoundary actionKey={descriptor.key}>
            <Suspense fallback={<div className="client-action-host client-action-host--loading">Loading…</div>}>
                <Component action={action} param={param} params={params} />
            </Suspense>
        </ClientActionErrorBoundary>
    );
    if (descriptor.layout && layoutPlan === "pending") {
        return <div className="client-action-host client-action-host--loading">Loading…</div>;
    }
    const body =
        descriptor.layout && layoutPlan && layoutPlan !== "pending" ? (
            <ClientActionLayoutRenderer
                plan={layoutPlan}
                onCommand={handleLayoutCommand}
                onRegisterRef={registerLayoutRef}
            >
                {componentNode}
            </ClientActionLayoutRenderer>
        ) : (
            componentNode
        );

    return (
        <_LifecycleCtx.Provider value={internal}>
            {body}
        </_LifecycleCtx.Provider>
    );
}

// ── Error boundary ──────────────────────────────────────────────────────────
// Without this, any render/import failure inside a client action bubbles to
// the router's default error component and takes down the whole page chrome.
// Chunk-hash failures auto-reload via lazyWithRetry before ever reaching
// here; what lands here is a real error — show it in place, keep the shell.

interface BoundaryProps {
    actionKey: string;
    children: ReactNode;
}

interface BoundaryState {
    error: Error | null;
}

class ClientActionErrorBoundary extends ReactComponent<BoundaryProps, BoundaryState> {
    state: BoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): BoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        console.error(`[client-action:${this.props.actionKey}] render failed`, error, info.componentStack);
    }

    render() {
        if (!this.state.error) return this.props.children;
        // A chunk-load failure (deploy happened while the tab was open) is
        // recoverable by a full reload — present it as such rather than a
        // raw stack. Genuine render errors keep the in-place detail + retry.
        const chunk = isChunkLoadError(this.state.error);
        return (
            <div className="client-action-host client-action-host--error">
                <div className="client-action-host__error-box">
                    <div className="client-action-host__error-title">
                        {chunk ? "A new version is available" : "This view failed to load"}
                    </div>
                    <div className="client-action-host__error-detail">
                        {chunk ? (
                            "This page was updated while your tab was open. Reload to get the latest version."
                        ) : (
                            <>
                                <span className="client-action-host__key">{this.props.actionKey}</span> — {this.state.error.message}
                            </>
                        )}
                    </div>
                    <div className="client-action-host__error-actions">
                        {chunk ? null : <button onClick={() => this.setState({ error: null })}>Try again</button>}
                        <button onClick={() => window.location.reload()}>{chunk ? "Reload now" : "Reload page"}</button>
                    </div>
                </div>
            </div>
        );
    }
}
